import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, euro, formatApiError } from "../lib/api";
import {
  Plus, Camera, Upload, Trash2, X, Sparkles, Receipt, Loader2,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  vendor_name: "",
  expense_date: new Date().toISOString().slice(0, 10),
  total_ttc: "",
  total_ht: "",
  vat_amount: "",
  category: "autre",
  description: "",
  payment_method: "carte",
};

const CONFIDENCE_LABELS = {
  high: { label: "Lecture fiable", color: "var(--sage)", Icon: CheckCircle2 },
  medium: { label: "À vérifier", color: "#B58B5C", Icon: AlertTriangle },
  low: { label: "Saisie manuelle conseillée", color: "#A04E3A", Icon: AlertTriangle },
};

export default function Depenses() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [scanning, setScanning] = useState(false);
  const [confidence, setConfidence] = useState(null);
  const [error, setError] = useState("");
  const [filterYear] = useState(new Date().getFullYear());
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [list, sum, cats] = await Promise.all([
        api.get(`/expenses?year=${filterYear}`),
        api.get(`/expenses/summary?year=${filterYear}`),
        api.get("/expenses/categories"),
      ]);
      setExpenses(list.data);
      setSummary(sum.data);
      setCategories(cats.data);
    } catch (e) {
      console.error("Failed to load expenses", e);
    }
  }, [filterYear]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(emptyForm);
    setConfidence(null);
    setError("");
    setShowForm(true);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setScanning(true);
    setError("");
    setConfidence(null);
    setShowForm(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({
        vendor_name: data.vendor_name || "",
        expense_date: data.invoice_date || new Date().toISOString().slice(0, 10),
        total_ttc: data.total_ttc ?? "",
        total_ht: data.total_ht ?? "",
        vat_amount: data.vat_amount ?? "",
        category: data.suggested_category || "autre",
        description: data.description || "",
        payment_method: "carte",
      });
      setConfidence(data.confidence || "medium");
      toast.success("Facture lue par l'IA — vérifiez puis enregistrez", { duration: 3500 });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      toast.error("L'IA n'a pas pu lire l'image. Saisissez les champs manuellement.");
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!form.vendor_name) return setError("Le nom du fournisseur est requis.");
    if (!form.total_ttc || Number(form.total_ttc) <= 0) return setError("Le montant TTC est requis.");
    try {
      await api.post("/expenses", {
        ...form,
        total_ttc: Number(form.total_ttc),
        total_ht: form.total_ht ? Number(form.total_ht) : null,
        vat_amount: form.vat_amount ? Number(form.vat_amount) : null,
      });
      setShowForm(false);
      toast.success("Dépense enregistrée");
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="depenses-container">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Dépenses fournisseurs</h1>
          <p className="text-muted-soft mt-1">
            Photographiez la facture · l'IA remplit tout pour vous.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="file-input"
          />
          <button onClick={() => cameraRef.current?.click()} className="btn-sage text-sm" data-testid="scan-camera-btn">
            <Camera className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Prendre en photo
          </button>
          <button onClick={() => fileRef.current?.click()} className="btn-ghost text-sm" data-testid="scan-upload-btn">
            <Upload className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Importer
          </button>
          <button onClick={openNew} className="btn-ghost text-sm" data-testid="add-manual-btn">
            <Plus className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Manuel
          </button>
        </div>
      </div>

      {/* Yearly summary card */}
      {summary && (
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="card-soft p-7" data-testid="summary-total-card">
            <div className="w-10 h-10 rounded-xl bg-beige flex items-center justify-center mb-3">
              <Receipt className="w-5 h-5" style={{ color: "#8B6F4D" }} strokeWidth={1.5} />
            </div>
            <div className="text-sm text-muted-soft mb-1">Total dépenses {summary.year}</div>
            <div className="font-display text-3xl">{euro(summary.yearly_total)}</div>
            <div className="text-xs text-muted-soft mt-1">{summary.count} facture{summary.count > 1 ? "s" : ""}</div>
          </div>

          <div className="card-soft p-7 sm:col-span-2" data-testid="summary-categories-card">
            <div className="text-sm font-medium mb-3">Top catégories</div>
            {summary.by_category.length === 0 ? (
              <div className="text-sm text-muted-soft">Aucune dépense pour l'instant. Photographiez votre première facture pour commencer.</div>
            ) : (
              <div className="space-y-2">
                {summary.by_category.slice(0, 5).map((c) => {
                  const pct = summary.yearly_total > 0 ? (c.amount / summary.yearly_total) * 100 : 0;
                  return (
                    <div key={c.key} className="flex items-center gap-3">
                      <div className="text-sm flex-1 truncate">{c.label}</div>
                      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--sage-light)" }}>
                        <div className="h-full bg-sage" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-sm font-medium w-24 text-right">{euro(c.amount)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* List */}
      {expenses.length === 0 ? (
        <div className="card-soft p-12 text-center" data-testid="expenses-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-sage-light flex items-center justify-center mb-4">
            <Sparkles className="w-7 h-7 text-sage" strokeWidth={1.4} />
          </div>
          <div className="font-display text-xl mb-2">Photographiez votre première facture</div>
          <p className="text-muted-soft max-w-md mx-auto">
            Notre IA lit le fournisseur, le montant, la TVA et la catégorie automatiquement. Plus de saisie manuelle.
          </p>
        </div>
      ) : (
        <div className="card-soft p-2 sm:p-4 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-soft">
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Fournisseur</th>
                <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Catégorie</th>
                <th className="text-right px-4 py-3 font-medium">Montant TTC</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-4 text-muted-soft">{e.expense_date}</td>
                  <td className="px-4 py-4 font-medium">
                    <div>{e.vendor_name}</div>
                    {e.description && <div className="text-xs text-muted-soft">{e.description}</div>}
                  </td>
                  <td className="px-4 py-4 hidden sm:table-cell">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-sage-light text-sage">{e.category_label}</span>
                  </td>
                  <td className="px-4 py-4 text-right font-medium">{euro(e.total_ttc)}</td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => remove(e.id)} className="p-2 rounded-full hover:bg-sage-light" data-testid={`expense-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-muted-soft" strokeWidth={1.6} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title={confidence ? "Vérifier les informations" : "Nouvelle dépense"} testid="expense-modal">
          {scanning ? (
            <div className="py-12 text-center">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-sage" />
              <div className="mt-4 font-display text-lg">L'IA analyse votre facture…</div>
              <div className="text-sm text-muted-soft mt-1">Quelques secondes seulement.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {confidence && (() => {
                const c = CONFIDENCE_LABELS[confidence] || CONFIDENCE_LABELS.medium;
                const I = c.Icon;
                return (
                  <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: "var(--sage-light)" }}>
                    <I className="w-4 h-4" style={{ color: c.color }} strokeWidth={1.8} />
                    <span className="text-sm">{c.label} — modifiez les champs si besoin avant de valider.</span>
                  </div>
                );
              })()}

              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Fournisseur">
                  <input className="input-soft" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="expense-vendor" />
                </Field>
                <Field label="Date">
                  <input type="date" className="input-soft" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date" />
                </Field>
                <Field label="Montant TTC (€)">
                  <input type="number" step="0.01" className="input-soft" value={form.total_ttc} onChange={(e) => setForm({ ...form, total_ttc: e.target.value })} data-testid="expense-ttc" />
                </Field>
                <Field label="Catégorie">
                  <select className="input-soft" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category">
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Montant HT (€) — optionnel">
                  <input type="number" step="0.01" className="input-soft" value={form.total_ht} onChange={(e) => setForm({ ...form, total_ht: e.target.value })} data-testid="expense-ht" />
                </Field>
                <Field label="TVA (€) — optionnel">
                  <input type="number" step="0.01" className="input-soft" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} data-testid="expense-vat" />
                </Field>
                <Field label="Description (optionnel)">
                  <input className="input-soft" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description" />
                </Field>
                <Field label="Paiement">
                  <select className="input-soft" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="expense-payment">
                    <option value="carte">Carte</option>
                    <option value="virement">Virement</option>
                    <option value="prelevement">Prélèvement</option>
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                  </select>
                </Field>
              </div>

              {error && <div className="text-sm text-clay">{error}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowForm(false)} className="btn-ghost text-sm" data-testid="expense-cancel">Annuler</button>
                <button onClick={submit} className="btn-sage text-sm" data-testid="expense-save">Enregistrer</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl card-soft p-6 max-h-[90vh] overflow-y-auto scroll-soft">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{title}</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-sage-light"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
